import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

interface SousService {
  id: number;
  name: string;
  [key: string]: any;
}

interface Service {
  id: number;
  name: string;
  agencyName?: string;
  imageUrl?: string;
  promoImg?: string;
  createdAt?: string;
  displayOrder?: number;
  orderIndex?: number;
  status?: string;
  sous_services?: SousService[];
  [key: string]: any;
}

@Component({
  selector: 'app-list-of-services',
  templateUrl: './list-of-services.component.html',
  styleUrls: ['./list-of-services.component.scss']
})
export class ListOfServicesComponent implements OnInit {
  idAgency: any;
  Agency: any;
  list: Service[] = [];
  isLoading = false;

  constructor(
    private agencieService: AgenciesService,
    private activatedRoute: ActivatedRoute,
    private serviceService: ServicesService,
    private awsService: AwscontrolService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
    if (!this.idAgency) {
      Swal.fire('Erreur', 'ID d’agence manquant', 'error');
      this.location.back();
      return;
    }
    this.loadAgencyServices();
  }

  loadAgencyServices(): void {
    this.isLoading = true;
    this.agencieService.getAgencieById(this.idAgency).subscribe({
      next: (data: any) => {
        this.Agency = data;
        const services = Array.isArray(data?.services) ? data.services : [];
        this.list = services.map((s: Service) => ({ ...s, sous_services: [] }));

        // Initialize order indexes and sort by displayOrder if present
        this.initializeOrderIndexes();

        if (this.list.length > 0) {
          this.loadSousServicesForServices();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire('Erreur', 'Impossible de charger les services.', 'error');
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(s => s.displayOrder !== undefined && s.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.list.sort((a, b) => {
        const dateA = this.parseDate(a.createdAt);
        const dateB = this.parseDate(b.createdAt);
        return dateA.getTime() - dateB.getTime();
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((service, idx) => {
      service.orderIndex = idx + 1;
      service.displayOrder = idx;
    });
  }

  private loadSousServicesForServices(): void {
    const requests = this.list.map(service => this.serviceService.getService(service.id));
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((fullService, index) => {
          this.list[index].sous_services = fullService?.sous_services || [];
        });
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erreur chargement sous‑services :', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Attention',
          text: 'Impossible de charger le nombre de sous‑services.',
          icon: 'warning',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  }

  private parseDate(dateString: string): Date {
    if (!dateString) return new Date(0);
    const parts = dateString.split(/[\s/:]+/);
    if (parts.length >= 6) {
      const [day, month, year, hour, minute, second] = parts.map(Number);
      return new Date(year, month - 1, day, hour, minute, second);
    }
    return new Date(dateString);
  }

  // ============================================================
  // ORDERING METHODS
  // ============================================================

  private saveAllOrders(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    this.list.forEach(service => {
      this.serviceService.updateServiceOrder(service.id, service.displayOrder!).subscribe({
        next: () => {
          completed++;
          if (completed === this.list.length && !hasError) {
            this.isLoading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 1500,
              icon: 'success',
              title: 'Ordre mis à jour'
            });
          }
        },
        error: (err) => {
          console.error(`Error updating order for service ${service.id}:`, err);
          hasError = true;
          this.isLoading = false;
          Swal.fire({
            title: 'Erreur',
            text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
            icon: 'error'
          });
          this.loadAgencyServices(); // reload to revert
        }
      });
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((service, idx) => {
      service.orderIndex = idx + 1;
      service.displayOrder = idx;
    });
    this.saveAllOrders();
  }

  drop(event: CdkDragDrop<Service[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE with AWS image removal
  // ============================================================

  deleteAgenciesService(service: Service, index: number): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le.'
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading()
        });

        // 1. Delete images from AWS
        this.deleteImagesFromS3(service)
          .then(() => {
            // 2. Delete service from backend
            return this.serviceService.deleteService(service.id).toPromise();
          })
          .then(() => {
            Swal.close();
            Swal.fire('Supprimé !', 'Le service et ses images ont été supprimés.', 'success');
            this.list.splice(index, 1);
            this.updateOrdersAndSave();
          })
          .catch((err) => {
            Swal.close();
            console.error('Erreur lors de la suppression:', err);
            // If image deletion fails, we still try to delete the service
            this.serviceService.deleteService(service.id).subscribe({
              next: () => {
                Swal.fire('Supprimé !', 'Le service a été supprimé (images non supprimées).', 'success');
                this.list.splice(index, 1);
                this.updateOrdersAndSave();
              },
              error: (err2) => {
                Swal.fire('Erreur', 'La suppression a échoué.', 'error');
                console.error(err2);
              }
            });
          });
      }
    });
  }

  private async deleteImagesFromS3(service: Service): Promise<void> {
    const imageUrls: string[] = [];

    if (service.imageUrl && service.imageUrl.trim() !== '') {
      imageUrls.push(service.imageUrl);
    }
    if (service.promoImg && service.promoImg.trim() !== '') {
      imageUrls.push(service.promoImg);
    }

    if (imageUrls.length === 0) {
      return;
    }

    const deletePromises = imageUrls.map(url =>
      this.awsService.deleteFile(url).toPromise().catch(err => {
        console.warn(`Failed to delete image ${url}:`, err);
        return Promise.resolve();
      })
    );

    await Promise.all(deletePromises);
  }

  deleteAgencies(service: Service, index: number): void {
    this.deleteAgenciesService(service, index);
  }

  // ============================================================
  // NAVIGATION
  // ============================================================

  navigateBack(): void {
    this.router.navigate(['/Agences-des-services']);
  }
}